import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  type DiscoveryApi,
  type FetchApi,
  type IdentityApi,
} from '@backstage/core-plugin-api';
import {
  EXTERNAL_CLIENTS_GROUP_REF,
  externalUserEntityRef,
  type ExternalClient,
  type ExternalUser,
} from './types';

async function getBaseUrl(discoveryApi: DiscoveryApi): Promise<string> {
  return discoveryApi.getBaseUrl('external-users');
}

export async function listExternalClients(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
): Promise<ExternalClient[]> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/clients`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error(`Falha ao listar clientes (${response.status})`);
  }
  const data = (await response.json()) as { clients: ExternalClient[] };
  return data.clients ?? [];
}

export async function createExternalClient(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  input: { name: string },
): Promise<ExternalClient> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/clients`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error || `Falha ao cadastrar cliente (${response.status})`);
  }
  const data = (await response.json()) as { client: ExternalClient };
  return data.client;
}

export async function deleteExternalClient(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  id: string,
): Promise<void> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/clients/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error || `Falha ao remover cliente (${response.status})`);
  }
}

export async function listExternalUsers(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
): Promise<ExternalUser[]> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error(`Falha ao listar usuários (${response.status})`);
  }
  const data = (await response.json()) as { users: ExternalUser[] };
  return data.users ?? [];
}

export async function createExternalUser(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  input: { clientId: string; email: string; password: string },
): Promise<ExternalUser> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error || `Falha ao cadastrar (${response.status})`);
  }
  const data = (await response.json()) as { user: ExternalUser };
  return data.user;
}

export async function updateExternalUser(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  id: string,
  input: { clientId: string; email: string; password?: string },
): Promise<ExternalUser> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error || `Falha ao atualizar (${response.status})`);
  }
  const data = (await response.json()) as { user: ExternalUser };
  return data.user;
}

export async function deleteExternalUser(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  id: string,
): Promise<void> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const { token } = await identityApi.getCredentials();
  const response = await fetchApi.fetch(`${baseUrl}/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error(`Falha ao remover usuário (${response.status})`);
  }
}

export async function loginExternalUser(
  discoveryApi: DiscoveryApi,
  input: { email: string; password: string },
): Promise<ExternalUser> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const response = await fetch(`${baseUrl}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error || 'Credenciais inválidas');
  }
  const data = (await response.json()) as { user: ExternalUser };
  return data.user;
}

export async function fetchExternalSession(
  discoveryApi: DiscoveryApi,
): Promise<ExternalUser | null> {
  const baseUrl = await getBaseUrl(discoveryApi);
  const response = await fetch(`${baseUrl}/me`, {
    credentials: 'include',
  });
  if (response.status === 401) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Falha ao validar sessão (${response.status})`);
  }
  const data = (await response.json()) as { user: ExternalUser };
  return data.user;
}

export async function logoutExternalUser(
  discoveryApi: DiscoveryApi,
): Promise<void> {
  const baseUrl = await getBaseUrl(discoveryApi);
  await fetch(`${baseUrl}/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function createExternalUserIdentity(
  user: ExternalUser,
  discoveryApi: DiscoveryApi,
): IdentityApi {
  const identity = {
    type: 'user' as const,
    userEntityRef: externalUserEntityRef(user.id),
    ownershipEntityRefs: [EXTERNAL_CLIENTS_GROUP_REF],
  };

  return {
    getProfileInfo: async () => ({
      displayName: user.clientName,
      email: user.email,
    }),
    getBackstageIdentity: async () => identity,
    getCredentials: async () => ({}),
    signOut: async () => {
      await logoutExternalUser(discoveryApi);
    },
  };
}

export { discoveryApiRef, fetchApiRef, identityApiRef };
